/* eslint-disable no-restricted-syntax */
import { toast } from 'react-toastify';
import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { ApiResponse } from 'api/ApiClient';
import { SuccessToastContainer } from 'components/Toast/ToastContainer';
import { IAction, IApiList, IDaySegment } from 'types';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../../api';
import * as Actions from './actions';
import { DaySegments, ICreateDaySegmentPayload, IDeleteDaySegmentPayload, IGenerateDaySegmentPayload, IGetDaySegmentOptions, IReplaceDaySegmentPayload, IUpdateDaySegmentPayload } from './types';

export function* getDaySegments({ payload }: IAction<DaySegments, IGetDaySegmentOptions>): any {
  try {
    const { siteId, options } = payload;
    const response: ApiResponse<IApiList<IDaySegment>> = yield call(API.Sites.getDaySegments, siteId, options);

    yield put(Actions.getDaySegmentsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getDaySegmentsError(error));
  }
}

export function* createDaySegment({ payload }: IAction<DaySegments, ICreateDaySegmentPayload>): any {
  try {
    const { siteId, data, onSuccess } = payload;
    const response: ApiResponse<IDaySegment> = yield call(API.Sites.createDaySegment, siteId, data);

    yield put(Actions.createDaySegmentSuccess(response.body));

    // Execute on success function
    if (onSuccess) yield call(onSuccess);

    // Show success toast
    yield call(toast, SuccessToastContainer({ message: 'Successfully added day segment' }));
  } catch (error: any) {
    yield put(Actions.createDaySegmentError(error, error.statusCode >= 500));
  }
}

export function* updateDaySegment({ payload }: IAction<DaySegments, IUpdateDaySegmentPayload>): any {
  try {
    const { siteId, daySegmentId, data, onSuccess } = payload;
    const response: ApiResponse<IDaySegment> = yield call(API.Sites.updateDaySegment, siteId, daySegmentId, data);

    yield put(Actions.updateDaySegmentSuccess(response.body));

    // Execute on success function
    if (onSuccess) yield call(onSuccess);

    // Show success toast
    yield call(toast, SuccessToastContainer({ message: 'Successfully updated day segment' }));
  } catch (error: any) {
    yield put(Actions.updateDaySegmentError(error, error.statusCode >= 500));
  }
}

export function* deleteDaySegment({ payload }: IAction<DaySegments, IDeleteDaySegmentPayload>): any {
  try {
    const { siteId, daySegmentId, onSuccess } = payload;
    const response: ApiResponse<IDaySegment> = yield call(API.Sites.deleteDaySegment, siteId, daySegmentId);

    yield put(Actions.deleteDaySegmentSuccess(response.body));

    // Execute on success function
    if (onSuccess) yield call(onSuccess);

    // Show success toast
    yield call(toast, SuccessToastContainer({ message: 'Successfully deleted day segment' }));
  } catch (error: any) {
    yield put(Actions.deleteDaySegmentError(error));
  }
}

export function* generateDaySegments({ payload }: IAction<DaySegments, IGenerateDaySegmentPayload>): any {
  try {
    const generatedDaySegmentsResponse: ApiResponse<IDaySegment[]> = yield call(API.Sites.generateDaySegments, payload.siteId);

    yield put(Actions.generateDaySegmentsSuccess());

    // Execute on success function
    if (payload.onSuccess) {
      yield call(payload.onSuccess, generatedDaySegmentsResponse.body);
    }
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    if (payload.onError) {
      yield call(payload.onError, formattedError);
    }
    yield put(Actions.generateDaySegmentsError(formattedError, false));
  }
}

export function* replaceDaySegments({ payload }: IAction<DaySegments, IReplaceDaySegmentPayload>): any {
  try {
    const daySegmentsResponse: ApiResponse<IApiList<IDaySegment>> = yield call(API.Sites.getDaySegments, payload.siteId);

    const createdDaySegments: IDaySegment[] = [];

    // Delete existing day segments
    for (const daySegment of daySegmentsResponse.body.data) {
      yield call(API.Sites.deleteDaySegment, payload.siteId, daySegment.id);
    }

    // Save suggested day segments
    for (const daySegment of payload.body) {
      const response: ApiResponse<IDaySegment> = yield call(API.Sites.createDaySegment, payload.siteId, daySegment);
      createdDaySegments.push(response.body);
    }

    yield put(Actions.replaceDaySegmentsSuccess(createdDaySegments));

    // Execute on success function
    if (payload.onSuccess) {
      yield call(payload.onSuccess);
    }

    // Show success toast
    yield call(toast, SuccessToastContainer({ message: 'Day segments saved' }));
  } catch (error: any) {
    yield put(Actions.replaceDaySegmentsError(error, true));
  }
}

export function* watchGetDaySegments() {
  yield takeEvery(DaySegments.GET_DAY_SEGMENTS_REQUEST, getDaySegments);
}

export function* watchCreateDaySegment() {
  yield takeEvery(DaySegments.CREATE_DAY_SEGMENT_REQUEST, createDaySegment);
}

export function* watchGenerateDaySegments() {
  yield takeEvery(DaySegments.GENERATE_DAY_SEGMENTS_REQUEST, generateDaySegments);
}

export function* watchReplaceDaySegments() {
  yield takeEvery(DaySegments.REPLACE_DAY_SEGMENTS_REQUEST, replaceDaySegments);
}

export function* watchUpdateDaySegment() {
  yield takeEvery(DaySegments.UPDATE_DAY_SEGMENT_REQUEST, updateDaySegment);
}

export function* watchDeleteDaySegment() {
  yield takeEvery(DaySegments.DELETE_DAY_SEGMENT_REQUEST, deleteDaySegment);
}

function* daySegmentsSaga() {
  yield all([fork(watchGetDaySegments), fork(watchCreateDaySegment), fork(watchUpdateDaySegment), fork(watchDeleteDaySegment), fork(watchGenerateDaySegments), fork(watchReplaceDaySegments)]);
}
export { daySegmentsSaga };
